import { Device } from "./Device"
import { FirmwareProductByID, StatusColors } from "./consts"
import { Version } from "../../modules/Version"
import type {
	FirmwarePreferences,
	BaseEidosType,
	BaseDeviceRawData,
	DeviceStatusAppearance,
	ProjectorPowerState,
} from "."
import { LuxedoRPC } from "luxedo-rpc"
import { ThirdPartyProjectorManager } from "../../data/SupportedProjectors"
import { Lightshow, Scene } from "../.."
import { interpretProjectorPowerState } from "../../modules/device-operation-managers/ProjectorPowerListener.svelte"
import { DataHandlerDevice } from "../../datahandlers/DataHandlerDevice"

// #region 		================================= 	TYPES 	=================================

export type StatusRPiDevice =
	| "OFF"
	| "NEW"
	| "UPDATING"
	| "IDLE"
	| "PROJECT"
	| "PROJ_TT"
	| "PNC"
	| "LOST"
	| "DORMANT"
	| "INIT"
	| "ASLEEP"
	| "WARMING_UP"
	| "READY"
	| "COOLING_DOWN"
	| string

export type Lux2LuxEidosStatus_Unknown = {
	role: "unknown"
	connected: false
}

export type Lux2LuxEidosStatus_Master = {
	role: "master"
	connected: boolean
	master_ip: string
	client_connections: { [id: number]: boolean }
}

export type Lux2LuxEidosStatus_Client = {
	role: "client"
	connected: boolean
	master_ip: string
}

export type Lux2LuxEidosStatus =
	| Lux2LuxEidosStatus_Unknown
	| Lux2LuxEidosStatus_Master
	| Lux2LuxEidosStatus_Client

export interface LuxcastFWPreferences extends FirmwarePreferences {
	show_wifi_info?: boolean
	audio_device?: "HDMI" | "HEADPHONES"
	projector_keep_alive_duration?: number
	hdmi_boost?: boolean
	hotspot_enabled?: boolean
	// Removed with firmware version 3.1.8
	// power_control_type?: "IR" | "RS232"
	// projector_manufacturer?: "OPTOMA" | "EPSON"
}

export interface EidosFirmware extends BaseEidosType {
	bluetooth?: {
		paired_entries: Array<number>
		scanned_entries: Array<number>
	}
	config?: {
		fw_config: LuxcastFWPreferences
	}
	display_config?: Array<number>
	display_mode?: string
	playback_type: "EMPTY" | "SCENE" | string
	message_queue?: Array<string>
	pnc?: {
		capture_id: number | "auto_cal"
		imgs_taken: number
		img_total: number
	}
	proj_id?: number
	proj_play_starttime?: number
	status: StatusRPiDevice
	projector_power?: {
		state: ProjectorPowerState // undefined is highly unlikely, so just treat it as OFF
		controller: "OptomaRS232Controller" | "EpsonRS232Controller" | "InfraredController"
	}
	lux2lux?: Lux2LuxEidosStatus | null
}

export interface DeviceRPiRawData extends BaseDeviceRawData {
	status: StatusRPiDevice
	type_id: "dev_luxedo" | "dev_luxcast"
	product_id: number
	proj_w?: number
	proj_h?: number
	version_crnt: string
	version_tgt?: string
	version_avail: string
	version_avail_stable: string
	password?: string
	third_party_proj?: string
	eidos: EidosFirmware
	preferences: LuxcastFWPreferences
}

export interface DeviceRPi extends Device<DeviceRPiRawData> {
	_eidos: EidosFirmware
	_status: StatusRPiDevice
	typeId: "dev_luxedo" | "dev_luxcast"
	isResolutionChanging?: boolean
	firmwareVersion: string
	thirdPartyProjector?: string
	orientation?: boolean
	password?: string
}

// #endregion ================================= 	TYPES 	=================================

export class DeviceRPi extends Device<DeviceRPiRawData> {
	// #region 		=================================  Initialization 	=================================

	declare orientation?: boolean
	declare availableUpdate?: string
	declare availableUpdateBeta?: string
	protected versionTarget?: string

	// FIX (Testing): Track previous state to detect transitions across eidos updates
	private __prevOnline?: boolean

	constructor(data: DeviceRPiRawData) {
		super(data)
		this._rawData = data
		// Initialize previous state trackers
		this.__prevOnline = this.isOnline
	}

	// #endregion =================================  Initialization 	=================================
	// #region 		=================================  Import / Export  =================================

	/**
	 * Imports the resolution depending on the device's product type.
	 */
	protected importResolution(data: DeviceRPiRawData) {
		if (data.type_id === "dev_luxedo") {
			this.resX = FirmwareProductByID[data.product_id].proj_res[0]!
			this.resY = FirmwareProductByID[data.product_id].proj_res[1]!
		} else {
			this.resX = data.proj_w!
			this.resY = data.proj_h!
		}
	}

	/**
	 * Imports the device data, populating this device instance
	 * @param data The device data
	 */
	protected importData(data: DeviceRPiRawData): void {
		super.importData(data)

		this.password = data.password
		this.orientation = !!parseInt(data.orientation ?? "0")
		this.firmwareVersion = data.version_crnt
		this.thirdPartyProjector = data.third_party_proj ?? "_all_other_projectors"

		this.availableUpdateBeta = data.version_avail
		this.availableUpdate = data.version_avail_stable ?? data.version_avail
	}

	/**
	 * Converts this object to a JSON representation (used to push changes)
	 * @returns
	 */
	protected exportData(): Partial<DeviceRPiRawData> {
		return {
			name: this.name,
			ui_color: this._color,
			proj_h: this.resY,
			proj_w: this.resX,
		}
	}

	// #endregion =================================  Import / Export  =================================
	// #region 		=================================   Device Update   =================================

	// because I cannot get the user data from here,
	// we save the "includeBeta" property, assuming that was
	// passed from the user's opt_in_beta status
	allowBeta?: boolean
	isUpdateAvailable(includeBeta?: boolean): boolean {
		this.allowBeta = includeBeta
		try {
			return (
				Version.compare_strings(
					this.firmwareVersion,
					includeBeta ? this.availableUpdateBeta : this.availableUpdate,
				) < 0
			)
		} catch (e) {
			console.warn("Error comparing device version to available versions.")
			return false
		}
	}

	async update(): Promise<void> {
		await LuxedoRPC.api.deviceControl.device_update(this.id!)
	}

	// #endregion =================================   Device Update   =================================
	// #region 		=================================      Getters      =================================

	/**
	 * Gets the online status of the Luxedo device
	 */
	get isOnline() {
		const status = this._status
		return (
			(status == "IDLE" || status == "PROJ_TT" || status == "PROJECT" || status == "PNC") &&
			this.tempEidos?.status !== "UPDATING"
		)
	}

	/**
	 * Checks if the device is busy or available for action
	 */
	get isReady() {
		return this._status == "IDLE" || this._status == "PROJ_TT" || this._status == "PROJECT"
	}

	/**
	 * Checks the eidos to verify the status of the internal projector
	 */
	get isProjectorOn() {
		if (!this.hasConnectedProjector || !this.eidos.projector_power || !this.eidos.projector)
			return false
		if (
			["ON", "POWERING_OFF"].includes(
				this.eidos.projector_power?.state ?? this.eidos.projector.state.actual,
			)
		)
			return true
		return false
	}

	/**
	 * Returns true if this device instance has an internal projector
	 */
	public get hasConnectedProjector() {
		return this instanceof DeviceRPi && this.typeId === "dev_luxcast"
	}

	/**
	 * Gets the lumen count of this device instance
	 * (NOT USED)
	 */
	get lumenCount() {
		if (
			!this.thirdPartyProjector ||
			!ThirdPartyProjectorManager.projectors[this.thirdPartyProjector]
		)
			return null
		return ThirdPartyProjectorManager.projectors[this.thirdPartyProjector].lumens ?? 3500
	}

	// #endregion =================================      Getters      =================================
	// #region 		=================================  Status Updates   =================================

	/**
	 * Called when the device's eidos is updated
	 * @param eidos
	 */
	onEidosUpdate(eidos: EidosFirmware): void
	onEidosUpdate(eidos: any): void
	onEidosUpdate(eidos: unknown): void {
		// FIX (Testing): Capture state before applying the new eidos
		const wasUpdating = this.tempEidos?.status === "UPDATING" || this._status === "UPDATING"
		const wasOnline = this.__prevOnline ?? this.isOnline

		super.onEidosUpdate(eidos)

		console.warn(`[${this.id!}] EIDOS UPDATE ->`, eidos, this)

		// After update, determine if we've transitioned back online and are no longer updating
		const nowOnline = this.isOnline
		const nowUpdating = this.tempEidos?.status === "UPDATING" || this._status === "UPDATING"

		// If we were updating or offline, and now we're online and not updating,
		// force a one-time fresh pull for this device so firmwareVersion and other
		// non-eidos fields refresh immediately (no page refresh required).
		if ((wasUpdating || !wasOnline) && nowOnline && !nowUpdating && this.id) {
			// Delay slightly to coalesce multiple rapid eidos events
			setTimeout(async () => {
				try {
					await DataHandlerDevice.pull([this.id!])
				} catch (err) {
					console.error("Failed to refresh device after update transition", err)
				}
			}, 500)
		}

		// Update previous state trackers for next eidos change
		this.__prevOnline = nowOnline
	}

	/**
	 * Gets the readable status object from this device's current status
	 * @returns DeviceStatusAppearance object
	 */
	public getStatus() {
		let status = this.tempEidos?.status ?? this._status
		// if status is not in idle state, don't process the projector power
		if (status !== "IDLE") return super.getStatus()

		// if status is in IDLE state, parse the projector power to determin the new state
		try {
			const projectorStatus = interpretProjectorPowerState(this.eidos)
			if (projectorStatus === "COOLING_DOWN" || projectorStatus === "POWERING_OFF")
				status = "COOLING_DOWN"
			else if (projectorStatus === "POWERING_ON" || projectorStatus === "WARMING_UP")
				status = "WARMING_UP"
			else if (projectorStatus === "OFF") status = "ASLEEP"
			else if (projectorStatus === "ON") status = "READY"
			else if ("projector" in this.eidos && "connection_interface" in this.eidos.projector)
				status = "UNKNOWN"
			else status = "IDLE"
		} catch (e) {
			console.log("Error while parsing device state", e)
		}

		return this.statusAppearanceMap[status]
	}

	/**
	 * A mapping of each eidos status w/ user facing text and a color
	 */
	protected statusAppearanceMap: Record<StatusRPiDevice, DeviceStatusAppearance> = {
		OFF: { text: "Offline", color: StatusColors.off },
		LOST: { text: "Offline", color: StatusColors.off },
		NEW: { text: "Offline", color: StatusColors.off },
		UPDATING: { text: "Updating...", color: StatusColors.updating },
		IDLE: { text: "Idle", color: StatusColors.idle },
		PROJECT: { text: "Playing", color: StatusColors.playing },
		PROJ_TT: { text: "Playing", color: StatusColors.playing },
		PNC: { text: "Capturing...", color: StatusColors.projectAndCapture },
		DORMANT: { text: "Searching...", color: StatusColors.initializing },
		INIT: { text: "Initialized", color: StatusColors.initializing },
		ASLEEP: { text: "Asleep", color: StatusColors.asleep },
		READY: { text: "Ready", color: StatusColors.idle },
		WARMING_UP: { text: "Waking...", color: StatusColors.initializing },
		COOLING_DOWN: { text: "Resting...", color: StatusColors.projectAndCapture },
		UNKNOWN: { text: "Unknown", color: StatusColors.idle },
	}

	// #endregion =================================  Status Updates   =================================
	// #region    ================================= Device Operations =================================

	// loadingPowerControl: Promise<void> | undefined = undefined

	/**
	 * Sends a projector power signal to the backend, resolves when the new projector state reflects the provided status.
	 * @param newPower The intended power signal of the internal projector
	 */
	async setProjectorPower(newPower: "ON" | "OFF"): Promise<void> {
		// if (this.loadingPowerControl) return this.loadingPowerControl
		return new Promise(async (res, rej) => {
			const resolve = (fail?: boolean) => {
				if (fail) rej()
				else res()
			}

			if (
				newPower === this.eidos.projector_power?.state ||
				newPower === this.eidos?.projector?.state.actual
			)
				return resolve()

			try {
				await LuxedoRPC.api.deviceControl.devops_set_projector_power(this.id!, newPower)
				await this.listenEidosCondition(
					(eidos) =>
						eidos.projector_power?.state == newPower || eidos.projector?.state?.actual == newPower,
					120,
				)
				resolve()
			} catch (e) {
				console.error("An error occurred while trying to set projector power.", e)
				resolve(true)
			}
		})
	}

	// #endregion ================================= Device Operations =================================

	// #region ================= Utility =================

	/**
	 * Checks if this device's fw version is at least the specified version
	 * @param fwVersion
	 */
	compareVersion(fwVersion: string) {
		const parseVersion = (version: string) => version.split(".").map(Number)

		if (!this.firmwareVersion) return undefined

		const [major1, minor1, patch1] = parseVersion(this.firmwareVersion)
		const [major2, minor2, patch2] = parseVersion(fwVersion)

		if (major1 !== major2) {
			return major1 > major2 ? 1 : -1
		}
		if (minor1 !== minor2) {
			return minor1 > minor2 ? 1 : -1
		}
		if (patch1 !== patch2) {
			return patch1 > patch2 ? 1 : -1
		}
		return 0
	}

	/**
	 * This method shows if the specified show is ready to play on this device
	 * @param show
	 * @returns
	 */
	isReadyToPlay(show: Lightshow | Scene): boolean {
		if (!this.eidos?.cache) return false
		if (!("shows" in this.eidos?.cache)) return true

		const cache = this.eidos.cache
		return !!cache.shows[show.id!]
	}

	/**
	 * This method shows if the specified show is actively being downloaded to this device
	 * @param show
	 * @returns
	 */
	isShowDownloading(show: Lightshow | Scene) {
		if (!this.eidos?.cache) return false

		const cache = this.eidos.cache
		if (!("currently_downloading" in cache)) return false
		if (cache.currently_downloading == show.id) return true
		return cache.download_queue.includes(show.id!)
	}

	/**
	 * This method shows if the version of the specified show is newer than the version found on this device
	 * @param show
	 * @returns
	 */
	isShowOutdated(show: Lightshow | Scene, fromGroup?: boolean): boolean {
		if (show.target_device_id && show.target_device_id != this.id && !fromGroup) return false
		let showVersion = show instanceof Lightshow ? show.updated_at : show.render_ver
		if (!showVersion) return false
		console.log(show)

		const cache = this.eidos?.cache
		if (!cache) return false
		if (!("shows" in cache)) return false

		const cachedShow = cache.shows[show.id!]
		if (!cachedShow) return true

		const cachedVersion = new Date((cachedShow.mtime + 30) * 1000)
		console.log("CACHED VERSION", cachedVersion)
		console.log("SHOW VERSION", showVersion)

		const outdated = cachedVersion < showVersion

		if (outdated || show instanceof Scene) {
			return outdated
		}

		// Check if the one of the scenes from the lightshow is outdated
		for (const content of show.getScenesInOrder()) {
			if (!(content instanceof Scene)) continue
			if (this.isShowOutdated(content, fromGroup)) {
				return true
			}
		}

		return false
	}

	// #endregion ============== Utility =================
}
